<script setup>
import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import EventChat from '../chat/EventChat.vue';
import SessionDialog from '../../livekit-client/components/SessionDialog.vue';
import XlButton from '../xl/XlButton.vue';
import { mySessionRow } from '../../api/portal';
import { viewerTimeZone } from '../../helpers/greeting';
import { joinFromAgenda } from '../../helpers/sessionJoin';
import { registerHubRoom } from '../../helpers/hubRoom';
import { reportError } from '../../faro';
import { useAuthStore } from '../../stores/auth';
import { useSessionStore } from '../../stores/sessionStore';
import { useSocketStore } from '../../stores/socketStore';
import '../../chat.scss';

/**
 * The room, hosted in the Agency Hub (Session: "Broker and Staff are sent into
 * the policyholder portal"). A session opened from Sessions or the Overview
 * opens over that page: the URL stays in the hub, nothing of the portal is
 * drawn, and closing the room leaves the page as it was — same tab, filter
 * and scroll, because it never went anywhere.
 *
 * What the portal's room host does for a policyholder (PortalSessionsRoom),
 * minus the portal: the chat socket before EventChat mounts (it runs the
 * room), the presence heartbeat, and a warning before a live call is left —
 * by closing the tab, or by leaving the hub's pages altogether. Moving between
 * hub pages keeps the call: this lives in the shell, not on a page.
 *
 * Who goes straight in is the room's own rule (helpers/roomEntry): the host,
 * hub staff and speakers do, with no join dialog and no waiting room.
 */
const router = useRouter();
const auth = useAuthStore();
const sessionStore = useSessionStore();
const socketStore = useSocketStore();

const ready = ref(false);
const leaveWarning = ref(false);
let pendingLeave = null;
let leaving = false;
let heartbeat = null;
let disposed = false;
let unregister = null;
let removeGuard = null;

/** The row the room needs, fetched by id; false when it is not this person's to open. */
const open = async (sessionId) => {
  const target = await mySessionRow(sessionId, viewerTimeZone());
  if (disposed || !target || typeof window.joinSessionEvent !== 'function') {
    window.loadNotification?.({ message: 'That session is no longer available.', error: true });
    return false;
  }
  try {
    await joinFromAgenda(target);
    return true;
  } catch (error) {
    reportError(error);
    window.loadNotification?.({ message: "The call couldn't be opened. Try again.", error: true });
    return false;
  }
};

// Leaving the hub's pages (the portal, signing out) ends the call: say so
// first. Between hub pages the call carries on, so those pass.
const guardLeaving = (to) => {
  if (leaving || !sessionStore.isConnected || to.meta?.admin) return true;
  pendingLeave = to;
  leaveWarning.value = true;
  return false;
};

const confirmLeave = () => {
  leaveWarning.value = false;
  leaving = true;
  router.push(pendingLeave ?? '/overview').finally(() => { leaving = false; });
};

const cancelLeave = () => {
  leaveWarning.value = false;
  pendingLeave = null;
};

const onBeforeUnload = (event) => {
  if (!sessionStore.isConnected) return undefined;
  event.preventDefault();
  event.returnValue = 'Are you sure you want to leave? You will be disconnected from the call.';
  return event.returnValue;
};

onMounted(async () => {
  removeGuard = router.beforeEach(guardLeaving);
  window.addEventListener('beforeunload', onBeforeUnload);

  try {
    await socketStore.connect();
  } finally {
    ready.value = true;
  }
  await nextTick();
  if (disposed) return;
  // EventChat has mounted and defined window.joinSessionEvent: rooms can open.
  unregister = registerHubRoom(open);

  heartbeat = setInterval(() => {
    const user = auth.userData;
    if (user?.id && socketStore.socket?.connected) {
      socketStore.socket.emit('heartbeat', { domain: window.store.subdomain, userId: user.id, timestamp: Date.now() });
    }
  }, 30000);
});

onBeforeUnmount(() => {
  disposed = true;
  unregister?.();
  removeGuard?.();
  clearInterval(heartbeat);
  window.removeEventListener('beforeunload', onBeforeUnload);
});
</script>

<template>
  <SessionDialog v-if="leaveWarning" title="You're about to leave the call" icon="leave" data-testid="leave-call-dialog">
    <p>Are you sure you want to leave the session? You will be disconnected from the call.</p>
    <template #actions>
      <XlButton variant="ghost" @click="cancelLeave">Cancel</XlButton>
      <XlButton data-testid="hub-leave-call-confirm" @click="confirmLeave">Leave</XlButton>
    </template>
  </SessionDialog>

  <div v-if="ready" id="omnichat-event" class="box-event hub-room" data-testid="hub-room">
    <EventChat hide-header />
  </div>
</template>

<style scoped>
/*
 * The room is a screen of its own (Office Hours: "Room opens over the hub page
 * with the sidebar and session list still showing around it"). The prototype
 * puts the room in place of the whole app for staff; the shared room frame
 * instead sat 63px down — the portal header's height — over a see-through
 * grey, so the hub's sidebar, header and list showed round it. In the hub it
 * covers the viewport, on the page's own background.
 */
.hub-room :deep(.modals-wrapper) {
  top: 0;
  height: 100vh;
  height: 100dvh;
  background-color: var(--xl-color-background);
}
</style>
